import React, { useEffect, useState } from 'react';
import EnhancedTable from 'src/components/enhanced-table/EnhancedTable';
import {
  EnhancedTableColumn,
  EnhancedTableColumnType,
  ITableMenuAction
} from 'src/components/enhanced-table';
import { DataService } from '../../services/data-service';
import Loader from 'src/components/Loader';
import { toast } from 'react-toastify';
import AddIcon from '@mui/icons-material/Add';
import VisibilityIcon from '@mui/icons-material/Visibility';
import AssignmentIcon from '@mui/icons-material/Assignment';
import InventoryIcon from '@mui/icons-material/Inventory';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ReceiptIcon from '@mui/icons-material/Receipt';
import DeleteIcon from '@mui/icons-material/Delete';
import { Box, Paper, Stack, Typography, alpha, useTheme } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import {
  getTransferOrderStatusLabel,
  TransferOrder,
  TransferOrderApiResponse,
  TransferOrdersApiResponse,
  TransferOrderStatus,
} from 'src/types/TransferOrder.types';
import { useAppDispatch, useAppSelector } from 'src/redux/hooks';
import { showAlert } from 'src/redux/alert/alertReducer';
import { OpenConfirmation } from 'src/redux/confirmation/confirmationReducer';
import {ROLE } from 'src/services/permission-service';
import StatusBadge from 'src/components/TransferOrder/StatusBadge';

const TransferOrderListing: React.FC = () => {
  const navigate = useNavigate();
  const [transferOrders, setTransferOrders] = useState<Record<string, TransferOrder>>({});
  const [loading, setLoading] = useState(true);
  const theme = useTheme();
  const dispatch = useAppDispatch();
  const userRole = useAppSelector(s => s.user.user.role) as ROLE;
  const userId = useAppSelector(s => s.user.user.id);

  const initialize = async () => {
    setLoading(true);
    try {
      const apiUrl = userRole === ROLE.CLIENT && userId
        ? `api/TransferOrder?clientId=${userId}`
        : 'api/TransferOrder';
      const res = await DataService.get(apiUrl);
      const response: TransferOrdersApiResponse = await res.json();
      const data: TransferOrderApiResponse[] = response.data || [];
      const result: Record<string, TransferOrder> = {};
      data.forEach((order) => {
        result[order.id] = {
          Id: order.id,
          ClientId: order.clientId,
          ClientName: order.clientName,
          OrderName: order.name,
          Status: order.status,
          Items: order.items || [],
          ItemCount: order.itemCount,
          TotalQuantity: order.totalQuantity,
          Notes: order.notes,
          CreatedAt: order.createdAt,
          UpdatedAt: order.updatedAt
        };
      });
      setTransferOrders(result);
    } catch (e) {
      console.error('Failed to fetch transfer orders:', e);
      toast.error('Failed to load transfer orders');
    }
    setLoading(false);
  };

  useEffect(() => {
    initialize();
  }, []);

  const handleStatusUpdate = async (orderId: string, newStatus: TransferOrderStatus) => {
    try {
      const req = await DataService.patch(`api/TransferOrder/${orderId}/status`, { status: newStatus });
      const resJson = await req.json();

      if (req.ok && resJson.success) {
        dispatch(showAlert({ message: `Transfer order status updated to ${getTransferOrderStatusLabel(newStatus)}`, type: 'success' }));
        initialize();
      } else {
        dispatch(showAlert({ message: resJson.message || 'Failed to update status', type: 'error' }));
      }
    } catch (error) {
      console.error('Failed to update status:', error);
      dispatch(showAlert({ message: 'Failed to update status', type: 'error' }));
    }
  };

  const headers: EnhancedTableColumn[] = [
    {
      id: 'OrderName',
      label: 'Order Name',
      type: EnhancedTableColumnType.TEXT,
    },
    {
      id: 'ClientName',
      label: 'Client',
      type: EnhancedTableColumnType.TEXT,
    },
    {
      id: 'ItemsCount',
      label: 'Items',
      type: EnhancedTableColumnType.TEXT,
    },
    {
      id: 'Status',
      label: 'Status',
      type: EnhancedTableColumnType.CUSTOM,
      customRender: (value: unknown, row?: any) => {
        const status =
          (value as number | undefined) ??
          (row?.Status as number | undefined);

        return (
          <StatusBadge
            status={(status ?? TransferOrderStatus.New) as TransferOrderStatus}
            type="order"
          />
        );
      },
    },
    {
      id: 'CreatedAt',
      label: 'Created',
      type: EnhancedTableColumnType.TEXT,
    },
    {
      id: 'actions',
      label: 'Actions',
      type: EnhancedTableColumnType.ACTION,
      actions: [
        {
          onClick: (id: string) => {
            navigate(`/transfer-orders/${id}`);
          },
          icon: <VisibilityIcon fontSize="small" />,
          label: 'View Details',
        },
        {
          onClick: (id: string) => {
            navigate(`/transfer-orders/${id}/allocate`);
          },
          icon: <AssignmentIcon fontSize="small" color="primary" />,
          label: 'Allocate Items',
          visible: (row: any) => row.Status === TransferOrderStatus.New,
        },
        {
          onClick: (id: string) => {
            navigate(`/transfer-orders/${id}/pick`);
          },
          icon: <InventoryIcon fontSize="small" color="warning" />,
          label: 'Start Picking',
          visible: (row: any) =>
            row.Status === TransferOrderStatus.Allocated ||
            row.Status === TransferOrderStatus.Picking,
        },
        {
          onClick: (id: string) => {
            navigate(`/transfer-orders/${id}/ship`);
          },
          icon: <LocalShippingIcon fontSize="small" color="info" />,
          label: 'Ship Order',
          visible: (row: any) =>
            row.Status === TransferOrderStatus.Ready ||
            row.Status === TransferOrderStatus.Picking,
        },
        {
          onClick: (id: string) => {
            navigate(`/transfer-orders/${id}`);
          },
          icon: <CheckCircleIcon fontSize="small" color="success" />,
          label: 'View Progress',
          visible: (row: any) =>
            row.Status === TransferOrderStatus.InTransit
        },
        {
          onClick: (id: string) => {
            navigate(`/client/receive-order/${id}`);
          },
          icon: <ReceiptIcon fontSize="small" color="secondary" />,
          label: 'Receive Order',
          visible: (row: any) =>
            userRole === ROLE.CLIENT &&
            (row.Status === TransferOrderStatus.InTransit ||
             row.Status === TransferOrderStatus.Received),
        },
        {
          onClick: (id: string) => {
            dispatch(OpenConfirmation({
              open: true,
              title: 'Delete Transfer Order',
              message: 'Are you sure you want to delete this transfer order?',
              onSubmit: async () => {
                try {
                  const response = await DataService.delete(`api/TransferOrder/${id}`, {});
                  const data = await response.json();

                  if (response.ok && (data.Success === true || data.success === true)) {
                    dispatch(showAlert({ message: 'Transfer order deleted successfully', type: 'success' }));
                    initialize();
                  } else {
                    const errorMessage = data.Message || data.message || 'Failed to delete transfer order';
                    dispatch(showAlert({ message: errorMessage, type: 'error' }));
                  }
                } catch (error) {
                  dispatch(showAlert({ message: 'Failed to delete transfer order', type: 'error' }));
                }
              }
            }));
          },
          icon: <DeleteIcon fontSize="small" color="error" />,
          label: 'Delete',
          visible: (row: any) => userRole !== ROLE.CLIENT && row.Status === TransferOrderStatus.New,
        }
      ]
    }
  ];

  const actions: ITableMenuAction[] = [
    {
      key: 'add',
      title: 'Create Transfer Order',
      icon: <AddIcon />,
      disabled: (selected: string[]) => false,
      onClick: () => {
        navigate('/new-transfer-order');
      },
    }
  ];

  const dataObj: Record<string, any> = Object.fromEntries(
    Object.entries(transferOrders).map(([id, order]) => [
      id,
      {
        ...order,
        OrderName: order.OrderName || '-',
        ItemsCount: order.ItemCount ?? order.Items.length,
        CreatedAt: order.CreatedAt ? new Date(order.CreatedAt).toLocaleDateString() : '-',
      }
    ])
  );

  const stats = {
    total: Object.keys(transferOrders).length,
    new: Object.values(transferOrders).filter(o => o.Status === TransferOrderStatus.New).length,
    allocated: Object.values(transferOrders).filter(o => o.Status === TransferOrderStatus.Allocated).length,
    picking: Object.values(transferOrders).filter(o => o.Status === TransferOrderStatus.Picking).length,
    ready: Object.values(transferOrders).filter(o => o.Status === TransferOrderStatus.Ready).length,
    inTransit: Object.values(transferOrders).filter(o =>
      o.Status === TransferOrderStatus.InTransit
    ).length,
    receiving: Object.values(transferOrders).filter(o =>
      o.Status === TransferOrderStatus.Received
    ).length,
    completed: Object.values(transferOrders).filter(o => o.Status === TransferOrderStatus.Completed).length,
  };

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="400px"
      >
        <Loader/>
      </Box>
    );
  }

  return (
    <Box>
      <Stack direction="row" spacing={2} sx={{ mb: 3, flexWrap: 'wrap' }}>
        <Paper
          sx={{
            p: 2,
            flex: 1,
            minWidth: '150px',
            background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)} 0%, ${alpha(theme.palette.primary.main, 0.05)} 100%)`,
            border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`
          }}
        >
          <Typography variant="h4" fontWeight={700} color="primary.main">
            {stats.total}
          </Typography>
          <Typography variant="body2" color="text.secondary" fontWeight={500}>
            Total Orders
          </Typography>
        </Paper>

        <Paper
          sx={{
            p: 2,
            flex: 1,
            minWidth: '150px',
            background: `linear-gradient(135deg, ${alpha(theme.palette.info.main, 0.1)} 0%, ${alpha(theme.palette.info.main, 0.05)} 100%)`,
            border: `1px solid ${alpha(theme.palette.info.main, 0.1)}`
          }}
        >
          <Typography variant="h4" fontWeight={700} color="info.main">
            {stats.new}
          </Typography>
          <Typography variant="body2" color="text.secondary" fontWeight={500}>
            New
          </Typography>
        </Paper>

        <Paper
          sx={{
            p: 2,
            flex: 1,
            minWidth: '150px',
            background: `linear-gradient(135deg, ${alpha(theme.palette.warning.main, 0.1)} 0%, ${alpha(theme.palette.warning.main, 0.05)} 100%)`,
            border: `1px solid ${alpha(theme.palette.warning.main, 0.1)}`
          }}
        >
          <Typography variant="h4" fontWeight={700} color="warning.main">
            {stats.picking + stats.ready}
          </Typography>
          <Typography variant="body2" color="text.secondary" fontWeight={500}>
            In Progress
          </Typography>
        </Paper>

        <Paper
          sx={{
            p: 2,
            flex: 1,
            minWidth: '150px',
            background: `linear-gradient(135deg, ${alpha(theme.palette.secondary.main, 0.1)} 0%, ${alpha(theme.palette.secondary.main, 0.05)} 100%)`,
            border: `1px solid ${alpha(theme.palette.secondary.main, 0.1)}`
          }}
        >
          <Typography variant="h4" fontWeight={700} color="secondary.main">
            {stats.inTransit + stats.receiving}
          </Typography>
          <Typography variant="body2" color="text.secondary" fontWeight={500}>
            In Transit
          </Typography>
        </Paper>

        <Paper
          sx={{
            p: 2,
            flex: 1,
            minWidth: '150px',
            background: `linear-gradient(135deg, ${alpha(theme.palette.success.main, 0.1)} 0%, ${alpha(theme.palette.success.main, 0.05)} 100%)`,
            border: `1px solid ${alpha(theme.palette.success.main, 0.1)}`
          }}
        >
          <Typography variant="h4" fontWeight={700} color="success.main">
            {stats.completed}
          </Typography>
          <Typography variant="body2" color="text.secondary" fontWeight={500}>
            Completed
          </Typography>
        </Paper>
      </Stack>

      <Paper sx={{ borderRadius: 2, overflow: 'hidden' }}>
        <EnhancedTable
          title="Transfer Orders"
          columns={headers}
          data={dataObj}
          actions={actions}
          collapsible={true}
          striped={true}
          boxedCells={true}
          wmsMode={true}
        />
      </Paper>
    </Box>
  );
};

export default TransferOrderListing;
